import React from 'react';
import { Tabs, usePathname, useRouter, Slot } from 'expo-router';
import { Colors, useThemeColors } from '../../constants/theme';
import { Ionicons } from '@expo/vector-icons';
import { Platform, View, StyleSheet } from 'react-native';
import { GlassView } from 'expo-glass-effect';
import { useAppStore } from '../../store/useAppStore';
import { firestoreService } from '../../services/firestoreService';
import { useMargenesSeguros } from '../../components/PantallaSegura';

// Plazo de la lectura del disco. AsyncStorage tarda milisegundos cuando
// responde; 3 s es holgado de sobra y sigue siendo menos de lo que nadie
// aguanta mirando una pantalla que no hace nada.
const ESPERA_HIDRATACION_MS = 3000;

export default function TabLayout() {
  const userRole = useAppStore((state) => state.userRole);
  const hasHydrated = useAppStore((state) => state._hasHydrated);
  const userFirebaseUid = useAppStore((state) => state.userFirebaseUid);
  const guestPreview = useAppStore((state) => state.guestPreview);
  // Inset REAL de la barra de navegacion: 48dp con 3 botones, ~24dp con gestos.
  const { abajo: margenAbajo } = useMargenesSeguros();
  // La barra de pestañas usaba `Colors`, que es un alias FIJO del tema claro
  // (theme.ts:38). Con Modo Oscuro encendido, el resto de la pantalla se ponía
  // oscura y la barra se quedaba clara. Con el hook sigue al tema de verdad.
  const colores = useThemeColors();
  const setRentCarDrivers = useAppStore((state) => state.setRentCarDrivers);
  const setRentCarVehicles = useAppStore((state) => state.setRentCarVehicles);
  const isOwner = userRole === 'RENT_CAR_OWNER';
  const isRenter = userRole === 'CAR_RENTER';
  const isDriver = userRole === 'DRIVER';
  const isFoodUser = userRole === 'FOOD_DELIVERY';
  // EL PASAJERO TAMBIÉN COMPRA. Ver la nota larga en la pestaña «Comida».
  const isPassenger = userRole === 'PASSENGER';
  // ⚠ `isOwner` es el dueño de RENT CAR, no el de un negocio. El nombre engaña y
  // por eso al dueño de un restaurante le salían las pestañas del pasajero: no
  // estaba en ninguna de las listas que esconden Viaje y Actividad.
  const isNegocio = userRole === 'RESTAURANT_OWNER';
  // El INVITADO también ve Comida: el catálogo de restaurantes es lo ÚNICO con
  // lectura pública en las reglas, o sea lo único que puede cargar de verdad
  // sin cuenta. Sin esto, un revisor de Google entra y no tiene NADA que probar.
  //
  // Ya NO se detecta por `userRole === 'GUEST'`: en el diseño actual el
  // invitado conserva su rol REAL (PASSENGER, DRIVER, …) y lo que lo marca es
  // la bandera `guestPreview`. Con el rol falso caía en cada `else` del
  // código y veía paneles que no le tocaban.
  //
  // Se lee del store con un selector (no con `esInvitado()`) porque esto es
  // render: si no está suscrito, las pestañas no se redibujan cuando el
  // invitado crea su cuenta.
  const isGuest = guestPreview && !userFirebaseUid;
  const pathname = usePathname();
  const router = useRouter();

  // ══════════════════════════════════════════════════════════════════════════
  // LA PUERTA DE LA HIDRATACIÓN TAMBIÉN TIENE RELOJ.
  //
  // Más abajo, `if (!hasHydrated) return null` corta el dibujado hasta que
  // Zustand termine de leer el disco. El propio store ya se blindó para abrir
  // la puerta aunque la lectura falle (ver `onRehydrateStorage`), pero eso solo
  // cubre lo que TERMINA: si `AsyncStorage.getItem` se queda colgado —disco
  // lleno, almacenamiento del sistema ocupado, un teléfono lento— la promesa no
  // resuelve ni rechaza, el callback no se llama nunca y la pestaña no monta
  // jamás. La pantalla queda en el color de fondo, sin error y sin salida.
  //
  // A los 3 s se dibuja igual. Arrancar sin lo guardado (el usuario vuelve a
  // ver el rol por defecto) es mucho mejor que no arrancar.
  //
  // OJO CON LO QUE **NO** HACE: al vencer el plazo NO se ejecutan los guardias
  // de rol de abajo. Siguen atados a `hasHydrated` a propósito — con el store
  // vacío, `userRole` es null, y expulsar a un dueño de su panel por un dato
  // que no pudimos leer sería inventarnos la respuesta. Se dibuja sin decidir;
  // si la hidratación llega tarde, el guardián corre entonces con datos reales.
  const [hidratacionVencida, setHidratacionVencida] = React.useState(false);
  React.useEffect(() => {
    if (hasHydrated) return;
    const t = setTimeout(() => {
      console.warn('[tabs/_layout] la lectura del estado guardado no respondió en 3 s; se arranca sin ella');
      setHidratacionVencida(true);
    }, ESPERA_HIDRATACION_MS);
    return () => clearTimeout(t);
  }, [hasHydrated]);

  React.useEffect(() => {
    // Si la memoria persistente de Zustand no ha cargado aún, no hacemos nada
    if (!hasHydrated) return;

    // GUARDIA DE SEGURIDAD ESTRICTO (Pilar 1)
    if (pathname === '/fleet' || pathname === '/dashboard' || pathname === '/drivers') {
      if (!isOwner) {
        router.replace('/partner');
      }
    }
    if (pathname === '/marketplace' || pathname === '/rent-chats') {
      if (!isRenter && !isOwner) {
        router.replace('/(tabs)/map');
      }
    }
    // H38 (auditoría 16-sep-2026) — la pestaña del mapa está OCULTA para estos
    // roles (href: null) pero sigue en el navegador, y el botón Atrás de las
    // pestañas vuelve a la primera ruta, que es justo esa. El cliente de comida,
    // el dueño y el arrendatario aterrizaban en "pedir viaje", una pantalla que
    // no tienen en su barra. Se les devuelve a la suya.
    //
    // V10: para estos roles las pestañas usan backBehavior 'none' (abajo), así
    // que Atrás ya no pasa por el mapa y esto queda solo para las llegadas
    // directas a /map (un enlace, un aviso). Con 'firstRoute' el REPLACE del
    // TabRouter dejaba el historial en [map, panel] y cada Atrás volvía aquí.
    if (pathname === '/map') {
      if (isFoodUser) router.replace('/(tabs)/food');
      else if (isOwner) router.replace('/(tabs)/dashboard');
      else if (isRenter) router.replace('/(tabs)/marketplace');
      // Su panel vive FUERA de las pestañas, por eso va sin el prefijo
      // `/(tabs)/`. Es la misma ruta que devuelve `rutaSegunRol`.
      else if (isNegocio) router.replace('/restaurant-dashboard');
    }
    // `isNegocio` también en las dependencias: sin él, el rescate no vuelve a
    // correr cuando el rol cambia y el dueño se queda en el mapa igual.
  }, [pathname, isOwner, isRenter, isFoodUser, isNegocio, router, hasHydrated]);

  React.useEffect(() => {
    // Escuchar la base de datos de Choferes y Vehículos en Tiempo Real
    // Usamos el UID real del usuario autenticado para aislar la flota por dueño.
    if (!hasHydrated || !isOwner || !userFirebaseUid) return;

    const unsubscribeDrivers = firestoreService.listenToCRMDrivers(userFirebaseUid, (drivers) => {
      setRentCarDrivers(drivers);
    });

    const unsubscribeVehicles = firestoreService.listenToOwnerVehicles(userFirebaseUid, (vehicles) => {
      setRentCarVehicles(vehicles);
    });

    return () => {
      unsubscribeDrivers();
      unsubscribeVehicles();
    };
  }, [hasHydrated, isOwner, userFirebaseUid, setRentCarDrivers, setRentCarVehicles]);

  // Bloquear el dibujado hasta que el estado se hidrate evita flashes y fugas
  // del mapa — pero con plazo (ver el comentario largo de arriba): a los 3 s se
  // monta igual, porque una pestaña que no monta nunca es un cuelgue.
  if (!hasHydrated && !hidratacionVencida) {
    return null;
  }

  // Si es Dueño de Rent Car en la Web, no renderizamos pestañas — el Sidebar se aplica
  // desde el root layout para que persista también en /wallet, /notifications, etc.
  if (Platform.OS === 'web' && isOwner) {
    return <Slot />;
  }

  // V10 — Atrás desde la pestaña propia del dueño, el arrendatario o el cliente
  // de comida no vuelve a la primera ruta (el mapa, que no tienen): con 'none'
  // el historial de las pestañas es solo la actual, GO_BACK no lo maneja nadie,
  // canGoBack() da false y entra el "toca Atrás otra vez para salir" de
  // _layout.tsx. Pasajero y chofer siguen como siempre.
  // ⚠ `isNegocio` TAMBIÉN VA AQUÍ, y olvidarlo dejaba el arreglo a medias.
  //
  // Esconder la pestaña con `href: null` NO quita la ruta del navegador — solo
  // le pone `display: 'none'` al botón. `map` sigue siendo la PRIMERA ruta, así
  // que con `backBehavior: 'firstRoute'` el botón Atrás se lo come el navegador
  // de pestañas y aterriza ahí: el dueño de un negocio acababa mirando el mapa
  // de pedir viaje, y encima con una barra de un solo botón, que es PEOR que
  // antes de esconderle las pestañas.
  //
  // Con 'none' el historial de pestañas es solo la actual, el Atrás burbujea a
  // la pila de arriba y lo devuelve a `/restaurant-dashboard`, que es de donde
  // vino. Es lo mismo que ya hacían el dueño de rent car, el arrendatario y el
  // cliente de comida.
  const sinVueltaAlMapa = isFoodUser || isOwner || isRenter || isNegocio;

  return (
    <Tabs
      backBehavior={sinVueltaAlMapa ? 'none' : 'firstRoute'}
      screenOptions={{
        headerShown: false,
        tabBarActiveTintColor: colores.primary,
        tabBarInactiveTintColor: colores.textSecondary,
        // `GlassView` (expo-glass-effect) SOLO existe en iOS: el paquete no trae
        // carpeta android y su respaldo (build/GlassView.js) devuelve un <View>
        // pelado. Como `glassBackground` no declaraba ningún color, en Android
        // la barra quedaba LITERALMENTE SIN FONDO y los iconos flotaban encima
        // del mapa y de las listas que se desplazan por debajo. Eso es lo que
        // se veía como "los menús se ponen feos" al entrar y salir de una
        // pantalla. En iOS se deja transparente para que el cristal se vea.
        tabBarBackground: () => (
          <GlassView
            style={[
              styles.glassBackground,
              Platform.OS === 'ios'
                ? null
                : {
                    backgroundColor: colores.card,
                    borderWidth: StyleSheet.hairlineWidth,
                    borderColor: colores.border,
                  },
            ]}
            colorScheme="light"
          />
        ),
        // `bottom` medido desde el inset REAL del teléfono, no a ojo.
        // Con edge-to-edge la barra flotante se dibujaba encima de los tres
        // botones del sistema: el tercio inferior de las pestañas quedaba
        // muerto, y en gestos se pegaba demasiado al borde. `margenAbajo` es
        // 48dp con barra de botones y ~24dp con gestos, así que se adapta solo.
        tabBarStyle: {
          position: 'absolute',
          bottom: margenAbajo + (Platform.OS === 'ios' ? 8 : 10),
          left: 20,
          right: 20,
          // Sin elevación, Android dibuja la barra por DEBAJO del contenido
          // que se desplaza. Con el fondo nuevo eso se notaría todavía más.
          elevation: Platform.OS === 'android' ? 8 : 0,
          borderTopWidth: 0,
          borderRadius: 24,
          height: 65,
          paddingBottom: Platform.OS === 'ios' ? 0 : 5,
          paddingTop: 5,
          backgroundColor: 'transparent',
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 10 },
          shadowOpacity: 0.1,
          shadowRadius: 15,
          // LA BARRA DE UN SOLO BOTON, FUERA.
          // A RESTAURANT_OWNER se le esconden 8 de las 9 pestanas, asi que le
          // quedaba una pastilla flotante de lado a lado con UN icono dentro.
          // El propio archivo ya lo admitia por escrito mas arriba: «y encima
          // con una barra de un solo boton, que es PEOR que antes de
          // esconderle las pestanas». Su casa es /restaurant-dashboard, que
          // vive FUERA de las pestanas, asi que esa barra no navegaba a nada.
          // No lo deja encerrado: la ida es el boton del panel
          // (restaurant-dashboard.tsx:1512) y la vuelta la tarjeta «Mi
          // Negocio» de su perfil. Las dos existen hoy.
          display: ((Platform.OS === 'web' && isOwner) || isNegocio) ? 'none' : 'flex',
        },
      }}>
      {/* Tab Viaje (mapa ride-hailing) — solo PASSENGER + roles que conducen.
          lazy: no monta el screen hasta que se navega a él la primera vez,
          ahorrando ~2s al cold-start para usuarios que arrancan en otra tab.
          NO usamos unmountOnBlur — destruir y re-crear el MapView nativo de
          Google Maps cada vez tomaba 2-3s y se sentía como una app lenta.
          En su lugar, dentro de map.tsx ocultamos el contenedor con
          display:'none' al perder foco, lo que libera el SurfaceView de
          Android sin necesidad de re-inicializar la sesión del mapa. */}
      <Tabs.Screen
        name="map"
        options={{
          title: 'Viaje',
          // El dueño de un negocio no pide viajes desde su cuenta de negocio:
          // «negocios son negocios» (fundador, 5-oct-2026). Si quiere viajar, usa
          // su cuenta de pasajero. Sin `isNegocio` aquí le salía la barra del
          // pasajero entera — Viaje, Actividad y Perfil — encima de una cuenta
          // rotulada «RESTAURANTE (DUEÑO)».
          href: isFoodUser || isOwner || isRenter || isNegocio ? null : '/(tabs)/map',
          tabBarIcon: ({ color, size, focused }: { color: import('react-native').ColorValue; size: number; focused: boolean }) => <Ionicons name={focused ? "car" : "car-outline"} size={size + 2} color={color} />,
          lazy: true,
        } as any}
      />
      {/* ══ LA PESTAÑA COMIDA LA VE TAMBIÉN EL PASAJERO (4-oct-2026) ══════
          Decía «solo FOOD_DELIVERY (PASSENGER ya NO la ve)». Eso dejaba al
          pasajero registrado en un callejón sin salida que el fundador
          describió así: «un usuario registrado de modo pasajero podría ver la
          parte de hacer pedido de comida porque se marca dentro como actividad
          pero no tiene la opción de conectarse a los restaurantes».

          Tenía toda la razón, y era peor de lo que parecía:
            · `activity.tsx` YA le consulta sus pedidos de comida al pasajero y
              le pinta el encabezado «Tus pedidos de comida» con el vacío «Aún
              no has hecho ningún pedido» — una promesa que no podía cumplir.
            · El INVITADO sin cuenta SÍ veía la pestaña y el pasajero
              registrado no. O sea que registrarse quitaba una función.
            · Y el cambio de modo es de un solo sentido: FOOD_DELIVERY puede
              pasarse a PASSENGER (profile.tsx), pero al revés no hay camino.

          POR QUÉ SE LE QUITÓ: por nada. Se buscó en el historial y viene de un
          commit grande de arreglos (c3230e5, 13-jul) cuyo mensaje no menciona
          la comida ni ningún rechazo de Play. Fue un cambio de pasada.

          POR QUÉ ES SEGURO: `food.tsx` no tiene NI UNA comprobación de rol,
          las reglas de `food_orders` tampoco miran el rol (el pedido lo crea
          el servidor) y `orders.ts` tampoco. O sea que el pasajero ya podía
          pedir de punta a punta: lo único que le faltaba era el botón.

          Y NO TOCA LOS VIAJES: la pestaña Viaje se decide en la línea de
          arriba con `isFoodUser || isOwner || isRenter ? null : '/(tabs)/map'`.
          PASSENGER no está en esa lista, así que conserva su mapa igual. */}
      <Tabs.Screen
        name="food"
        options={{
          title: 'Comida',
          href: isFoodUser || isGuest || isPassenger ? '/(tabs)/food' : null,
          tabBarIcon: ({ color, size, focused }) => <Ionicons name={focused ? "fast-food" : "fast-food-outline"} size={size + 2} color={color} />,
        }}
      />
      {/* Tab Actividad — historial de viajes (PASSENGER/DRIVER) y pedidos (FOOD_DELIVERY) */}
      <Tabs.Screen
        name="activity"
        options={{
          title: 'Actividad',
          // Para un negocio esta pestaña sale VACÍA: `activity.tsx` consulta los
          // pedidos por `customerId` (el que COMPRA), nunca por el dueño del
          // negocio. Sus pedidos los ve en su panel, que es donde se atienden.
          href: isOwner || isRenter || isNegocio ? null : '/(tabs)/activity',
          tabBarIcon: ({ color, size, focused }) => <Ionicons name={focused ? "time" : "time-outline"} size={size + 2} color={color} />,
        }}
      />
      <Tabs.Screen
        name="dashboard"
        options={{
          title: 'Dashboard',
          href: isOwner ? '/(tabs)/dashboard' : null,
          tabBarIcon: ({ color, size, focused }) => <Ionicons name={focused ? "stats-chart" : "stats-chart-outline"} size={size + 2} color={color} />,
        }}
      />
      <Tabs.Screen
        name="fleet"
        options={{
          title: 'Flota',
          href: isOwner ? '/(tabs)/fleet' : null,
          tabBarIcon: ({ color, size, focused }: { color: import('react-native').ColorValue; size: number; focused: boolean }) => <Ionicons name={focused ? "car" : "car-outline"} size={size + 2} color={color} />,
        }}
      />
      <Tabs.Screen
        name="drivers"
        options={{
          title: 'Choferes',
          href: isOwner ? '/(tabs)/drivers' : null,
          tabBarIcon: ({ color, size, focused }) => <Ionicons name={focused ? "people" : "people-outline"} size={size + 2} color={color} />,
        }}
      />
      <Tabs.Screen
        name="marketplace"
        options={{
          title: 'Marketplace',
          href: isRenter ? '/(tabs)/marketplace' : null,
          tabBarIcon: ({ color, size, focused }: { color: import('react-native').ColorValue; size: number; focused: boolean }) => <Ionicons name={focused ? "car" : "car-outline"} size={size + 2} color={color} />,
        }}
      />
      <Tabs.Screen
        name="rent-chats"
        options={{
          title: 'Chats',
          // ⚠️ ESTA PESTAÑA SE LE ENSEÑABA AL LADO EQUIVOCADO.
          // La pantalla está escrita para el DUEÑO (usa
          // `listenToOwnerNegotiations`, que busca por el prefijo del id, donde
          // va el uid del dueño) y sin embargo el `href` solo se la daba al
          // CHOFER. Así que el chofer entraba a una lista que no podía
          // encontrar nada, y el dueño —que es quien tiene que contestar— no
          // tenía por dónde entrar en el teléfono: su único acceso era una
          // barra lateral que solo se dibuja en web de más de 768 px.
          // Los mensajes llegaban y no los leía nadie.
          href: isRenter || isOwner ? '/(tabs)/rent-chats' : null,
          tabBarIcon: ({ color, size, focused }) => <Ionicons name={focused ? "chatbubbles" : "chatbubbles-outline"} size={size + 2} color={color} />,
        }}
      />
      <Tabs.Screen
        name="profile"
        options={{
          title: 'Perfil',
          href: '/(tabs)/profile',
          tabBarIcon: ({ color, size, focused }) => <Ionicons name={focused ? "person" : "person-outline"} size={size + 2} color={color} />,
        }}
      />
    </Tabs>
  );
}

const styles = StyleSheet.create({
  glassBackground: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    borderRadius: 24,
    overflow: 'hidden',
  }
});
